// ══════════════════════════════════════════════════════════════════════════
//  [WP37-R3 2026-08-11] Fable trust math — ONE implementation, shared browser↔server
//  (the src/training-mode.js precedent). api/db.js's read-only `fable-trust-global`
//  action, the classifier's current-project slice, the unit suite and the deployed
//  selftest all import THIS module, so the ratified band boundaries cannot drift
//  between the platform rollup and the project slice. Imports only the shared
//  training law (the never-learn predicate must be THE shared one, per ratification).
// ══════════════════════════════════════════════════════════════════════════
import { isTrainingRecord } from "./training-mode.js";

// The since-date is grounded by the DATA, not by a filter: per-line fableReview
// persistence began with WP-M1 (merged 2026-08-11), so no earlier record can carry
// the field — the `if (!f)` guard below IS the date boundary.
export const FABLE_TRUST_SINCE = "2026-08-11";

// Blake-ratified bands (2026-08-11), anchored to the Wave-3 race evidence.
// Boundary semantics, pinned in tests + selftest: 74.9→60–74 · 75→75–89 · 89.9→75–89 · 90→90+.
export const FABLE_TRUST_BANDS = ["90+", "75–89", "60–74", "<60"];
export const fableTrustBand = confidence => {
  // null/undefined/blank are NOT zero-confidence: Number(null) === 0 would silently file an
  // unmeasured verdict under "<60" as though it had been measured below the bar. [WP37-R4]
  const c = confidence == null || confidence === "" ? NaN : Number(confidence);
  if (!Number.isFinite(c)) return null;
  return c >= 90 ? "90+" : c >= 75 ? "75–89" : c >= 60 ? "60–74" : "<60";
};
const emptyTrustBands = () => Object.fromEntries(FABLE_TRUST_BANDS.map(b => [b, { n: 0, agree: 0 }]));

// Accepts both record shapes: corpus decision records ({fableReview, final:{...}}) and live
// browser rows ({_fableReview, humanDecision, result}). Latest capturedAt snapshot wins per
// project line because decision logs are append-only and Mark Complete may re-flush a line.
// NOTE on field semantics: in the `flags` buckets, `agree` counts flags that WON — the human
// final differed from the pre-flag call (`was`). The UI labels that column "Flags won".
// `unbanded` counts verdicts that qualified but carry no finite confidence — they can buy
// review (fableFlagRoutes routes null-confidence flags) yet cannot be banded, so they are
// counted LOUDLY here rather than silently dropped.
export function computeFableTrust(records = []) {
  const latest = new Map();
  for (const r of records || []) {
    if (!r || isTrainingRecord(r) || isTrainingRecord({ training: r._training })) continue;
    const key = `${r.projectId || ""}\u0000${r.ro || ""}\u0000${r.line || ""}`;
    const prior = latest.get(key);
    if (!prior || String(r.capturedAt || "") >= String(prior.capturedAt || "")) latest.set(key, r);
  }
  const hr = emptyTrustBands(); const flags = emptyTrustBands(); const projects = new Set();
  let lines = 0; let unbanded = 0;
  for (const r of latest.values()) {
    const f = r.fableReview || r._fableReview;
    const humanDecision = r.final?.humanDecision ?? r.humanDecision;
    const finalResult = r.final?.result ?? r.result;
    if (!f || !humanDecision || (finalResult !== "Include" && finalResult !== "Exclude")) continue;
    const band = fableTrustBand(f.confidence);
    const isHrOpinion = f.mode === "hr" && (f.wouldBe === "Include" || f.wouldBe === "Exclude");
    const isFlag = f.mode === "auto" && f.flag === true && (f.was === "Include" || f.was === "Exclude");
    if (!band) { if (isHrOpinion || isFlag) unbanded++; continue; }
    let counted = false;
    if (isHrOpinion) { hr[band].n++; if (f.wouldBe === finalResult) hr[band].agree++; counted = true; }
    if (isFlag) { flags[band].n++; if (f.was !== finalResult) flags[band].agree++; counted = true; }
    if (counted) { lines++; if (r.projectId) projects.add(r.projectId); }
  }
  return { since: FABLE_TRUST_SINCE, projects: projects.size, lines, unbanded, hr, flags };
}

